import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import SafariListPage from '../../pages/SafariListPage';
import { getExtraInformation } from '../../server/getExtraInformation';
import { getCountries } from '../../server/getCountries';
import { getSafaris } from '../../server/getSafaris';
import { destinationMiddleware } from '../../server/middleware/destinationMiddleware';
import { sanitizeHtml } from '../../server/utlis/sanitizeHtml';
import { stripParagraphBr } from '../../server/utlis/stripParagraphBr';
import { replaceDestinationName } from '../../server/utlis/replaceDestinationName';
import { generateBreadcrumbSchema } from '../../utils/jsonLd';

const loadDestination = createServerFn()
    .middleware([destinationMiddleware])
    .handler(async ({ context }) => {
        return context.destination;
    });

export const Route = createFileRoute('/safaris/')({
    beforeLoad: async () => {
        try {
            const destination = await loadDestination();
            return { destination };
        } catch (error) {
            console.error('Failed to load destination:', error);
            throw error;
        }
    },
    loader: async ({ context }) => {
        const { destination } = context;
        const countryId = typeof destination?.country === 'object'
            ? destination?.country?._id
            : destination?.country || null;
        const [extraInfo, countriesList, initialSafarisData] = await Promise.all([
            getExtraInformation({ data: { displayPage: 'Safari' } }).catch(() => null),
            getCountries().catch(() => []),
            countryId && destination?._id
                ? getSafaris({
                    data: {
                        destination: destination._id,
                        country: countryId,
                        page: 1,
                    },
                }).catch(() => ({}))
                : Promise.resolve({}),
        ]);

        if (extraInfo) {
            if (Array.isArray(extraInfo.steps)) {
                extraInfo.steps = await Promise.all(
                    extraInfo.steps.map(async (step) => ({
                        ...step,
                        description: await sanitizeHtml({
                            data: {
                                html: await replaceDestinationName({
                                    data: {
                                        text: await stripParagraphBr({
                                            data: { html: step.description },
                                        }),
                                        destinationName: destination?.name,
                                    },
                                }),
                            },
                        }),
                    }))
                );
            }

            extraInfo.description = await stripParagraphBr({ data: { html: extraInfo.description } });
            extraInfo.description = await replaceDestinationName({
                data: { text: extraInfo.description, destinationName: destination?.name },
            });
            extraInfo.title = await replaceDestinationName({
                data: { text: extraInfo.title, destinationName: destination?.name },
            });
            extraInfo.description = await sanitizeHtml({ data: { html: extraInfo.description } });
        }

        return { destination, extraInfo, countriesList, initialSafarisData };
    },
    // Server-rendered — see routes/index.jsx for why this replaces Helmet.
    head: ({ loaderData }) => {
        const destination = loaderData?.destination;
        const title =
            destination?.metaData?.safariPage?.title ||
            `${destination?.name || 'Maasai Mara'} | Book Safaris Directly`;
        const description =
            destination?.metaData?.safariPage?.description ||
            `Are you looking for your dream safari in ${destination?.name || 'Maasai Mara'}? We have just what you are looking for and more! Contact safari operators in ${destination?.name || 'Maasai Mara'} directly.`;
        const image = destination?.images?.safari?.[0]?.url;
        const siteUrl = (destination?.url || 'https://www.maasaimara.com').replace(/\/$/, '');
        const canonicalUrl = `${siteUrl}/safaris`;

        const breadcrumbSchema = generateBreadcrumbSchema(
            [
                { title: 'Home', url: '/' },
                { title: 'Safaris', url: '/safaris' },
            ],
            siteUrl
        );

        return {
            meta: [
                { title },
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: canonicalUrl },
                ...(image
                    ? [
                        { property: 'og:image', content: image },
                        { property: 'og:image:type', content: 'image/jpeg' },
                    ]
                    : []),
            ],
            links: [
                { rel: 'canonical', href: canonicalUrl },
            ],
            scripts: [
                ...(breadcrumbSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(breadcrumbSchema) }]
                    : []),
            ],
        };
    },
    component: SafariListPage,
    // ── Caching config 
    staleTime: 5 * 60 * 1000,   // reuse cached data for 5 mins on re-navigation
    gcTime: 10 * 60 * 1000,  // keep in memory for 10 mins after route unmounts
});